import { useId, type ReactElement } from "react";
import { ChevronDown, ChevronUp, Trash2 } from "lucide-react";
import {
  widgetTargetKey,
  type TimelineMilestone,
  type TimelineMilestoneKind,
} from "@playbookai/widget-schemas";
import { DatePicker } from "../../molecules/DatePicker/DatePicker.js";
import { ActionMenu } from "../../molecules/ActionMenu/ActionMenu.js";
import { useFieldEditSession } from "../field-edit-session.js";
import { KIND_LABEL } from "./presentation.js";

const FIELD =
  "w-full rounded-md border border-pb-control-border bg-pb-card px-3 py-2 text-sm text-pb-text";

export function MilestoneDetails({
  milestone,
  index,
  count,
  readOnly,
  onPatch,
  onDelete,
  onMove,
}: {
  readonly milestone: TimelineMilestone;
  readonly index: number;
  readonly count: number;
  readonly readOnly: boolean;
  readonly onPatch: (patch: Partial<TimelineMilestone>) => void;
  readonly onDelete: () => void;
  readonly onMove: (delta: number) => void;
}): ReactElement {
  const dateId = useId();
  /*
   * PB-4923 AC5 — every leaf of this milestone, `date` included. It is edited
   * through the DS `DatePicker`, which reports its OPEN lifetime rather than
   * DOM focus: the trigger blurs as soon as the calendar takes focus, and an
   * outside press closes without blurring it at all, so a focus-bound claim
   * would drop mid-decision. `endDate` is a mapped field with no control on
   * this surface, so there is nothing to claim for it.
   */
  const target = (field: string): string =>
    widgetTargetKey([
      { kind: "item", collection: "milestones", itemId: milestone.id },
      { kind: "prop", name: field },
    ]);
  const labelLease = useFieldEditSession(target("label"));
  const dateLease = useFieldEditSession(target("date"));
  const kindLease = useFieldEditSession(target("kind"));
  const estimatedLease = useFieldEditSession(target("estimated"));
  const projectedLease = useFieldEditSession(target("projected"));
  const flagLease = {
    estimated: estimatedLease,
    projected: projectedLease,
  } as const;
  return (
    <section
      aria-label="Selected milestone"
      className="min-w-0 rounded-lg border border-pb-border bg-pb-card-elevated/30 p-4"
    >
      <header className="mb-4 flex items-center justify-between gap-2">
        <h3 className="text-sm font-medium text-pb-text-secondary">
          Selected milestone
        </h3>
        {!readOnly && (
          <ActionMenu
            triggerLabel={`Milestone actions for ${milestone.label}`}
            triggerSize="sm"
            items={[
              {
                id: "up",
                label: `Move ${milestone.label} up`,
                icon: ChevronUp,
                ...(index === 0 ? { disabledReason: "Already first" } : {}),
                onSelect: () => onMove(-1),
              },
              {
                id: "down",
                label: `Move ${milestone.label} down`,
                icon: ChevronDown,
                ...(index === count - 1
                  ? { disabledReason: "Already last" }
                  : {}),
                onSelect: () => onMove(1),
              },
              {
                id: "delete",
                label: `Delete milestone ${milestone.label}`,
                icon: Trash2,
                tone: "critical",
                separatorBefore: true,
                onSelect: onDelete,
              },
            ]}
          />
        )}
      </header>
      <div className="flex flex-col gap-4">
        <label className="flex min-w-0 flex-col gap-1.5 text-xs text-pb-text-muted">
          Event
          <input
            aria-label={`${milestone.label} label`}
            value={milestone.label}
            maxLength={255}
            disabled={readOnly}
            {...labelLease}
            onChange={(event) => onPatch({ label: event.target.value })}
            className={FIELD}
          />
        </label>
        <div className="min-w-0">
          <label
            htmlFor={dateId}
            className="mb-1.5 block text-xs text-pb-text-muted"
          >
            Date<span className="sr-only"> for {milestone.label}</span>
          </label>
          <DatePicker
            id={dateId}
            value={milestone.date}
            disabled={readOnly}
            placeholder="Pick a date"
            {...dateLease}
            onChange={(date) => onPatch({ date })}
          />
        </div>
        <label className="flex flex-col gap-1.5 text-xs text-pb-text-muted">
          Type
          <select
            value={milestone.kind}
            disabled={readOnly}
            {...kindLease}
            onChange={(event) =>
              onPatch({ kind: event.target.value as TimelineMilestoneKind })
            }
            className={FIELD}
          >
            {Object.entries(KIND_LABEL).map(([kind, label]) => (
              <option key={kind} value={kind}>
                {label}
              </option>
            ))}
          </select>
        </label>
        {(["estimated", "projected"] as const).map((flag) => (
          <label
            key={flag}
            className="flex items-center gap-2 text-xs text-pb-text-muted"
          >
            <input
              type="checkbox"
              aria-label={`${milestone.label} ${flag}`}
              checked={milestone[flag] ?? false}
              disabled={readOnly}
              {...flagLease[flag]}
              onChange={(event) => onPatch({ [flag]: event.target.checked })}
            />
            {flag === "estimated" ? "Estimated" : "Projected"}
          </label>
        ))}
      </div>
    </section>
  );
}
